
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Address } from '../lib/interfaces/generic/Address';
import FiltersView from './filters-view.vue';
import { container } from '../lib/container';
import { Actions, ActionStates } from '../lib/Actions';
import { ContainerTypes } from '../lib/core/types/ContainerTypes';
import { I18n } from '../lib/interfaces/I18n';
import deDE from '../lib/presets/resources/de-DE';
import AutoCompleteSuggestion = ym.AutoCompleteSuggestion;
import { setCmd, execCmd } from '../lib/core/storage/CommandStorage';
import { getConfig } from '../lib/ConfigLoader';
import { PositionRequestData } from '../lib/interfaces/search/RequestData';
import { lteDevice, ltTabletDevice, notDevice } from '../lib/core/responsive';
import { SearchStorage } from '../lib/core/storage/SearchStorage';
import { GPS } from 'src/lib/core/GPS';
import { AppBehavior } from 'src/lib/core/AppBehavior';

const actions = container.get<Actions>(ContainerTypes.Action);
const storage = container.get<SearchStorage>(ContainerTypes.SearchStorage);
const gps = container.get<GPS>('gps');
const behavior = container.get<AppBehavior>('behavior');

actions.on(ActionStates.Idle, (from, event) => execCmd('searchIdle', from, event));

actions.on(ActionStates.HomePage, () => execCmd('resetToHome'));

@Component({ components: { FiltersView } })
export default class SearchView extends Vue {
	static generateAwesomepleteMark(item: any, query: string): string {
		return item.replace(RegExp(Awesomplete.$.regExpEscape(query.trim()), 'gi'), '<mark>$&</mark>').trim();
	}

	@Prop({ required: true })
	provider: any;

	$refs!: {
		searchTerm: HTMLInputElement;
		filters: FiltersView;
	};

	i18n: I18n = deDE;

	value!: string;
	hasValue = false;
	branches!: string[];
	address!: Address;

	isMobileFiltersVisible = false;
	isMobileFiltersActive = false;
	hasRectangle = false;
	fromAutocomplete = false;

	xhr!: XMLHttpRequest | null;

	get showInputLocalisationButton(): boolean {
		return behavior.appView == false;
	}

	get showResetButton(): boolean {
		return this.hasValue;
	}

	get isAppView(): boolean {
		return this.$store.state.isAppView;
	}

	get geoSearch(): boolean {
		const isMobile = ltTabletDevice();
		return !isMobile || this.$store.getters.isSearchVisible;
	}

	isNotMobile() {
		return notDevice('mobile');
	}

	get showFiltersView(): boolean {
		return (this.isNotMobile() || this.isMobileFiltersVisible) && this.geoSearch;
	}

	toggleFilters() {
		this.isMobileFiltersVisible = !this.isMobileFiltersVisible;
		this.isMobileFiltersActive = this.isMobileFiltersVisible || storage.filters.length > 0;
	}

	setValue(value: string) {
		this.$nextTick(() => {
			this.$refs.searchTerm.value = value;
			this.hasValue = value !== '';
			this.value = value;
			storage.searchTerm = value;
		});
	}

	changedValue(event: Event) {
		this.setValue((event.target as HTMLInputElement).value);
	}

	changedFilters(branchCodes: string[], dataFilters: string[]) {
		if (branchCodes.length) {
			this.branches = [...branchCodes];
		} else {
			this.branches = [...getConfig().searchRequest.branches];
		}

		let filters: string = '';
		if (dataFilters.length) {
			filters = `(${this.branches.join('|')})&(${dataFilters.join(')&(')})`;
		}

		if (this.value && this.value.trim() !== '') {
			if (actions.canGo(ActionStates.SearchFilter)) {
				actions.go(ActionStates.SearchFilter, {
					type: 'Rectangle',
					location: this.value,
					branches: this.branches || [],
					// Wir wollen alle Branchencodes integrieren, ABER wir wollen, dass jeder DataFilter
					// mit einer UND Verknüpfung seine Bedingungen definiert.
					dataFilters: filters
				});
			}
		}
	}

	submit(event?: Event) {
		if (event) {
			event.preventDefault();
		}

		this.fromAutocomplete = false;
		this.hasRectangle = false;

		this.$nextTick(() => {
			storage.searchTerm = this.value;
			actions.go(ActionStates.SearchInput, {
				type: 'Location',
				location: this.value,
				branches: this.branches || []
			});

			if (lteDevice('tablet')) {
				this.$refs.searchTerm.blur();
			}
		});
	}

	localizeInput(event?: Event) {
		if (event) {
			event.preventDefault();
		}

		this.fromAutocomplete = false;
		this.hasRectangle = false;

		gps.localize();
	}

	resetSearchInput(): void {
		if (this.value) {
			this.setValue('');
		}
	}

	emptySearchInput(): void {
		this.resetSearchInput();
		this.$refs.searchTerm.focus();
		this.$nextTick(() => {
			actions.go(ActionStates.CallResults, {
				type: 'Location',
				location: this.value,
				branches: this.branches || []
			});
		});
	}

	onGPSLocalized(data: { center: ym.provider.LatLngLiteral; addressLine: string }): void {
		this.setValue(data.addressLine);
		actions.go(ActionStates.SearchInput, {
			type: 'Position',
			branches: this.branches || [],
			center: data.center
		} as PositionRequestData);
	}

	onGPSError(err: GeolocationPositionError | Error): void {
		if (err instanceof GeolocationPositionError) {
			// https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPositionError
			switch (err.code) {
				case 1:
					// TODO: User denied page access to geolocation
					// TODO: System denied page access to geolocation
					alert('CashPool  ist nichtberechtigt, Ihren Standort zu verwenden. TEST');
					alert('Error: ' + err.message);
					console.error('Error: ', { err });
					break;
				case 2:
					// TODO: Position not found
					alert('Ihr Standort ist aktuell nicht verfügbar.');
					break;
				case 3:
					// TODO: Geolocation went into a timeout
					alert('CashPool konnte Ihren Standort nicht ermitteln.');
					break;
			}
		} else {
			console.error(err);
		}
	}

	created() {
		gps.events.on('localized', this.onGPSLocalized);
		gps.events.on('error', this.onGPSError);

		this.branches = getConfig().searchRequest.branches;
		this.$nextTick(() => {
			if (this.$refs.searchTerm) {
				this.$refs.searchTerm.focus();
			}
		});
	}

	destroyed() {
		gps.events.off('localized', e => this.onGPSLocalized(e));
		gps.events.off('error', e => this.onGPSError(e));
	}

	mounted() {
		setCmd('searchIdle', (from, event) => (this.hasRectangle = true));
		setCmd('resetToHome', () => this.resetSearchInput());
		this._enableGeoAutocomplete();
		this.$emit('mounted', this.$el);
	}

	private _enableGeoAutocomplete() {
		const suggestionList: AutoCompleteSuggestion[] = [];
		const config = getConfig();

		// Watch out, adventurer! You enter the dangerous swamp of autocomplete.
		const autocomplete = ym.autoComplete(this.$refs.searchTerm as HTMLInputElement, {
			dataType: 'jsonp',
			locales: config.autocomplete.locales,

			onEmpty: () => {
				this.address = {
					isoCountry: config.defaultCountryCode
				};
			},

			onSuggestion: (geoJSON: any) => {
				if (this.fromAutocomplete) return;
				const location = geoJSON.features[0].properties;
				this.address = {
					singleSlot: location.displayValue,
					country: location.country,
					isoCountry: location.country || 'DE',
					district: location.district,
					zip: location.zip,
					city: location.city,
					cityPart: location.cityPart,
					streetNo: location.street
				};

				suggestionList.splice(0);
				for (const feature of geoJSON.features) {
					suggestionList.push(feature.properties);
				}
			},
			onSelected: (geoJSON: any) => {
				if (this.fromAutocomplete) {
					this.setValue(this.address.singleSlot || '');
					return;
				}
				this.fromAutocomplete = true;

				const location = geoJSON.properties.location;
				this.address = {
					singleSlot: geoJSON.properties.displayValue,
					country: location.country,
					isoCountry: location.country,
					district: location.district,
					zip: location.zip,
					city: location.city,
					cityAddon: location.cityAddOn,
					cityPart: location.cityPart,
					streetNo: location.street
				};
				this.setValue(geoJSON.properties.displayValue);

				setTimeout(() => {
					this.fromAutocomplete = false;
					this.submit();
				}, 500);
			}
		});

		// this is a workaround to manipulate the suggestion list items
		const awesomplete = (autocomplete as any)._autocomplete;

		awesomplete.item = (item: { label: string; value: number }, query: string) => {
			const index = item.value;
			let html;
			if (query.trim() === '') {
				html = item;
			} else {
				const above = SearchView.generateAwesomepleteMark(item, query);
				html = `<div class="awesomplete-item">${above}</div>`;
			}

			return Awesomplete.$.create('li', {
				innerHTML: html,
				'aria-selected': 'false',
				id: 'awesomplete_list_' + index + '_item'
			});
		};
	}
}
